/* Levering og henting på produktsiden (plan 19d-1). Logikken: Admin docs/varesystem/logikk/levering-10.md.
 *
 * Bare lesing: GET <Lager>/api/levering/oppslag med og uten postnummer, aldri skriving til kurven (det gjør handlekurvboksen,
 * assets/levering-valg.js). Postnummeret lagres i nettleseren (localStorage gg_postnummer) bare når kunden skriver det eller trykker
 * «Velg min lokasjon» (ekomloven § 3-15 bokstav b). Alternativene lagres aldri: hver sidevisning og hver åpning av panelet slår opp på nytt
 * (Lager lar nettleseren bruke svaret i 60 s), så en aktivering i Lager-admin vises på neste visning etter høyst et minutt. Svarer ikke
 * Lager, vises henting og en rolig melding, aldri gamle priser. Navn, priser og tekster for hjemlevering kommer fra oppslaget, aldri herfra.
 * Retning A (faner) valgt av Emanuel 2026-10-31.
 *
 * 19d-2, postnummeret på kunden (plan 19d «Postnummeret lagres på kunden»): innlogget kunde som skriver eller velger lokasjon får
 * postnummeret lagret i Lager med POST /apps/gg/postnummer (proxyen bestemmer kunden, aldri en ID herfra), og neste side leser det fra
 * kundens metafelt lager.postnummer (Liquid). Regelen ved innlogging: et postnummer kunden skrev i dette besøket og som Lager ikke har
 * bekreftet ennå (sessionStorage gg_postnummer_venter, merket som gjest eller med denne kunden), sendes og vinner; merket fjernes når Lager
 * har bekreftet. Ellers vinner kundens postnummer (GET /apps/gg/kunde er fasit, metafeltet er første visning), og nettleserens postnummer
 * erstattes av det. */
if (!customElements.get('levering-produkt')) {
  const NOKKEL = 'gg_postnummer';
  const FRIST_MS = 6000;
  const VENT_MS = 300;
  const MED = ['klar', 'neste', 'ingen_dager', 'ingen_sone', 'utilgjengelig'];
  const KJENT = ['klar', 'neste', 'ingen_dager', 'ingen_sone']; // Lager har bekreftet postnummeret (codex 19d-1, 7)
  // Hjelperne under er kopiert fra assets/levering-valg.js (handlekurvboksen), så datoer, priser og stedsnavn ser like ut begge steder.
  const sprak = document.documentElement.lang || 'nb';
  const fmt = (o) => new Intl.DateTimeFormat(sprak, { ...o, timeZone: 'UTC' });
  const ukedagLang = fmt({ weekday: 'long' });
  const datoLang = fmt({ day: 'numeric', month: 'long' });
  const tallFmt = new Intl.NumberFormat(sprak, { maximumFractionDigits: 2 });
  const kroner = (ore) => `${tallFmt.format(ore / 100)} kr`;
  const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
  const fyll = (mal, v) => String(mal || '').replace(/\[(\w+)\]/g, (_, k) => v[k] ?? '');
  const stedsnavn = (s) => String(s || '').toLowerCase().replace(/(^|[\s-])(\p{L})/gu, (m, f, b) => f + b.toUpperCase()).replace(/ I /g, ' i ');
  const d12 = (dato) => new Date(`${dato}T12:00:00Z`);
  const dagTekst = (dato) => `${ukedagLang.format(d12(dato))} ${datoLang.format(d12(dato))}`; // «mandag 2. november»
  const gyldigSvar = (s) => s && typeof s.postnummer === 'string' && Array.isArray(s.dager) && s.vilkar && typeof s.vilkar.versjon === 'number'
    && (s.sone === null || (typeof s.sone.id === 'number' && typeof s.sone.pris_ore === 'number'));

  // Kampanjen «gratis levering» (serie 23, 2026-11-01, Admin docs/varesystem/logikk/fri-frakt-kampanje.md), samme regel som i
  // assets/levering-valg.js: aktiv = på og dagens dato i Oslo til og med sluttdatoen; gratis(pris) = aktiv og summen minst grensen og prisen
  // til og med taket. Summen her er kurven (cart.total_price) pluss den valgte variantens pris: varen kunden ser på kvalifiserer alene når den
  // koster over grensen. Innstillingene kommer fra snippets/kampanje-frakt-cfg.liquid.
  const osloFmt = new Intl.DateTimeFormat('en', { timeZone: 'Europe/Oslo', year: 'numeric', month: '2-digit', day: '2-digit' });
  const osloDato = (d) => {
    const p = Object.fromEntries(osloFmt.formatToParts(d).filter((x) => x.type !== 'literal').map((x) => [x.type, x.value]));
    return `${p.year}-${p.month}-${p.day}`;
  };
  // Datoen må være en ekte kalenderdato («2026-99-99» aktiverer ingenting, codex 23b, 1)
  const gyldigDato = (v) => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(v || '')) return false;
    const d = new Date(`${v}T12:00:00Z`);
    return Number.isFinite(d.getTime()) && d.toISOString().slice(0, 10) === v; // «2026-99-99» gir Invalid Date, aldri RangeError (codex 23b runde 2, 1)
  };
  const kampanjeAktiv = (k) => !!(k && k.pa === true && gyldigDato(k.til) && k.grense_ore > 0 && k.tak_ore >= 0
    && k.prisord && osloDato(new Date()) <= k.til);
  const kampanjeGratis = (k, sumOre, prisOre) => kampanjeAktiv(k) && Number.isFinite(sumOre) && sumOre >= k.grense_ore
    && Number.isFinite(prisOre) && prisOre <= k.tak_ore;
  // Millisekunder til neste midnatt i Oslo (som i levering-valg.js): en åpen side tegnes på nytt etter datoskiftet (codex 23b, 3)
  const osloKlokke = new Intl.DateTimeFormat('en', { timeZone: 'Europe/Oslo', hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23' });
  const msTilMidnatt = (d) => {
    const idag = osloDato(d);
    const [y, m, dag] = idag.split('-').map(Number);
    const k = Object.fromEntries(osloKlokke.formatToParts(d).filter((x) => x.type !== 'literal').map((x) => [x.type, Number(x.value)]));
    const naa = Math.floor(d.getTime() / 1000) * 1000;
    // Oslo-tidens avstand til UTC akkurat nå, så midnatt regnes i Oslo-tid; ved sommertidsskifte prøves timen før og etter og den som
    // faktisk er første øyeblikk med ny dato velges (codex 23b runde 2, 3)
    const avstand = Date.UTC(y, m - 1, dag, k.hour % 24, k.minute, k.second) - naa;
    const gjett = Date.UTC(y, m - 1, dag + 1) - avstand;
    for (const kandidat of [gjett, gjett + 3600000, gjett - 3600000]) {
      if (osloDato(new Date(kandidat)) !== idag && osloDato(new Date(kandidat - 1000)) === idag) return Math.max(kandidat - d.getTime(), 0) + 1500;
    }
    return (86400 - ((k.hour % 24) * 3600 + k.minute * 60 + k.second)) * 1000 + 1500;
  };

  const PROXY_FRIST_MS = 15000;
  const MERKE = 'gg_postnummer_venter'; // sessionStorage: { pn, k } der k er null (skrevet som gjest) eller kundemarkøren
  const gyldigPn = (v) => (typeof v === 'string' && /^\d{4}$/.test(v) ? v : '');
  const lesMerke = () => {
    try {
      const m = JSON.parse(window.sessionStorage.getItem(MERKE) || 'null');
      return m && gyldigPn(m.pn) && (m.k === null || typeof m.k === 'string') ? m : null;
    } catch (e) {
      return null;
    }
  };
  const settMerke = (pn, k) => {
    try {
      window.sessionStorage.setItem(MERKE, JSON.stringify({ pn, k }));
    } catch (e) {
      // blokkert lagring: postnummeret gjelder bare denne siden, og innloggingen tar det ikke med
    }
  };
  /** Fjerner merket, men bare når det fortsatt gjelder postnummeret som ble bekreftet (en nyere skriving i samme fane står). */
  const fjernMerke = (pn) => {
    try {
      const m = lesMerke();
      if (!m || pn == null || m.pn === pn) window.sessionStorage.removeItem(MERKE);
    } catch (e) {
      // blokkert lagring
    }
  };
  // Som uuid() i assets/favoritter.js
  const uuid = () => {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') return window.crypto.randomUUID();
    const b = window.crypto.getRandomValues(new Uint8Array(16));
    b[6] = (b[6] & 0x0f) | 0x40;
    b[8] = (b[8] & 0x3f) | 0x80;
    const h = Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('');
    return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
  };
  /** Kall gjennom app proxyen med frist til hele svaret er lest. Gir { status, ok, data }, status 0 ved nettfeil eller frist. */
  const proxyKall = async (url, opts = {}) => {
    const ctrl = new AbortController();
    const tid = setTimeout(() => ctrl.abort(), PROXY_FRIST_MS);
    try {
      const r = await fetch(url, { credentials: 'same-origin', cache: 'no-store', ...opts, signal: ctrl.signal, headers: { Accept: 'application/json', ...(opts.headers || {}) } });
      let data = null;
      try {
        data = await r.json();
      } catch (e) {
        data = null;
      }
      return { status: r.status, ok: r.ok, data };
    } catch (e) {
      return { status: 0, ok: false, data: null };
    } finally {
      clearTimeout(tid);
    }
  };

  const lesLagret = () => {
    try {
      const v = window.localStorage.getItem(NOKKEL);
      return /^\d{4}$/.test(v || '') ? v : '';
    } catch (e) {
      return '';
    }
  };
  const lagrePostnummer = (pn) => {
    try {
      window.localStorage.setItem(NOKKEL, pn);
    } catch (e) {
      // privat modus eller blokkert lagring: postnummeret gjelder bare denne siden
    }
  };

  class LeveringProdukt extends HTMLElement {
    connectedCallback() {
      if (this.startet) return;
      this.startet = true;
      const cfg = JSON.parse(this.querySelector('[data-lp-cfg]').textContent);
      this.tk = cfg.tekster;
      this.innlogget = cfg.innlogget === true;
      this.kunde = typeof cfg.kunde === 'string' ? cfg.kunde : null;
      this.proxy = cfg.proxy || '/apps/gg';
      // Kampanjen (serie 23): kurvsummen fra Liquid ved sidelasting, oppdatert fra temaets kurvhendelser; varens pris fra den valgte varianten,
      // oppdatert ved variantbytte på dette produktet.
      this.kampanje = cfg.kampanje && typeof cfg.kampanje === 'object' ? cfg.kampanje : null;
      this.produktId = cfg.produkt_id;
      this.kurvSum = Number.isFinite(cfg.kurv_sum) ? cfg.kurv_sum : NaN;
      this.varePris = Number.isFinite(cfg.vare_pris) ? cfg.vare_pris : 0;
      // pn er postnummeret som gjelder (fire sifre), utkast det kunden skriver før det er fire. visning: «med» viser sammendraget i boksen.
      // gen øker hver gang postnummeret byttes, så et svar fra GET kunde aldri overstyrer et postnummer kunden har valgt etterpå.
      this.t = { pn: this.startPostnummer(cfg), svar: null, status: 'tom', visning: 'uten', utkast: null, sokId: 0, lagreFor: null, fraId: 0, fraPris: null, fraFeil: false, fraVersjon: null, lok: null, lokNr: 0, gen: 0 };
      if (this.t.pn) this.t.visning = 'med';
      this.fane = 'lever';
      // Panelet flyttes til body: en forelder med transform eller overflow ville ellers klippe det (position: fixed). Et eksemplar fra en
      // tidligere tegning av blokken fjernes først, så det aldri står to paneler med samme id.
      this.panel = this.panel || this.querySelector('#lp-panel');
      document.querySelectorAll('body > #lp-panel').forEach((p) => {
        if (p === this.panel) return;
        if (p.hasAttribute('open') && typeof p.close === 'function') p.close();
        p.remove();
      });
      document.body.appendChild(this.panel);

      this.vedSkriving = (e) => { if (e.target.matches('[data-lp-postnummer]')) this.skrev(e.target); };
      this.vedKlikk = (e) => {
        const b = e.target.closest('button');
        if (!b) return;
        if (b.hasAttribute('data-lp-apne')) this.apne(b, b.dataset.lpApne === 'felt');
        else if (b.hasAttribute('data-lp-lokasjon')) this.finnLokasjon();
        else if (b.dataset.lpFane) this.velgFane(b.dataset.lpFane, false);
      };
      this.vedTast = (e) => {
        const b = e.target.closest('[data-lp-fane]');
        if (!b) return;
        const rekke = ['lever', 'hent'];
        const i = rekke.indexOf(b.dataset.lpFane);
        const ny = { ArrowRight: rekke[(i + 1) % 2], ArrowLeft: rekke[(i + 1) % 2], Home: rekke[0], End: rekke[1] }[e.key];
        if (!ny) return;
        e.preventDefault();
        this.velgFane(ny, true);
      };
      this.vedLukking = () => {
        // Et halvt skrevet postnummer gjelder ikke: feltene viser postnummeret som gjelder igjen, og det slås opp på nytt, fordi
        // utkastet forkastet oppslaget som var underveis (codex 19d-1, 5)
        if (this.t.utkast == null) return;
        this.t.utkast = null;
        if (this.t.pn) this.velgPostnummer(this.t.pn, false);
        else {
          this.t.status = 'tom';
          this.tegn();
        }
      };
      this.vedLagring = (e) => {
        // Postnummeret er byttet i en annen fane: neste visning her følger det, men aldri mens kunden skriver
        if (e.key !== NOKKEL || !/^\d{4}$/.test(e.newValue || '') || e.newValue === this.t.pn) return;
        if (document.activeElement && document.activeElement.matches('[data-lp-postnummer]')) return;
        this.t.visning = 'med';
        this.velgPostnummer(e.newValue, false);
      };
      this.vedTilbake = (e) => {
        if (!e.persisted) return; // tilbake-knappen med sidebufferen er en ny visning: slå opp igjen
        // Et valg fra før siden ble lagt i sidebufferen gjelder ikke lenger: ventende tidtaker, ønsket om å lagre og en POST som ikke er
        // sendt, forkastes, så et oppslag nå aldri lagrer et postnummer på en kunde som logget inn etterpå (codex 19d-2 runde 2)
        clearTimeout(this.vent);
        this.t.lagreFor = null;
        this.onsket = null;
        this.t.lokNr++; // et posisjonssøk som var underveis, gjelder ikke (codex 19d-2 runde 3)
        this.t.lok = null;
        this.hentKurvSum(); // kurven kan være endret på kurvsiden mens denne siden lå i sidebufferen (codex 23b, 2)
        this.hentFraPris();
        if (this.t.pn) this.slaOpp(this.t.pn, false);
        // Et ventende merke sendes ikke herfra: siden i sidebufferen kan være tegnet for en annen kunde enn den som er logget inn nå
        // (kontobytte i fanen), og proxyen lagrer på den som er logget inn. Neste vanlige sidelasting sender det (codex 19d-2, 3).
        if (this.innlogget && !lesMerke()) this.hentKunde();
      };
      for (const rot of [this, this.panel]) {
        rot.addEventListener('input', this.vedSkriving);
        rot.addEventListener('click', this.vedKlikk);
      }
      this.addEventListener('keydown', this.vedTast);
      this.vedForLukking = () => {
        this.vedLukking(); // utkastet forkastes med en gang, ikke etter animasjonen (codex 19d-1 runde 2, 3)
        // Er knappen som åpnet panelet tegnet på nytt eller skjult (postnummeret ble ukjent, feltet står igjen), får det som står der fokus
        const synlig = (el) => !!(el && el.isConnected && el.getClientRects().length);
        if (!synlig(this.panel.opener)) {
          this.panel.opener = [this.querySelector(this.apnetMed), this.querySelector('[data-lp-uten] [data-lp-postnummer]'), ...this.querySelectorAll('[data-lp-apne]')].find(synlig) || null;
        }
      };
      this.panel.addEventListener('on:levering-produkt:before-close', this.vedForLukking);
      window.addEventListener('storage', this.vedLagring);
      window.addEventListener('pageshow', this.vedTilbake);
      // Kampanjen: variantbytte på dette produktet gir ny varepris, «lagt i kurven» og antallsendring gir ny kurvsum (temaets hendelser,
      // assets/custom.js). Hurtigkjøp av et annet produkt sender også on:variant:change, derfor sjekkes produkt-ID-en.
      this.vedVariant = (e) => {
        const d = e.detail || {};
        if (!d.variant || !d.product || String(d.product.id) !== String(this.produktId)) return;
        if (!Number.isFinite(d.variant.price)) return;
        this.varePris = d.variant.price;
        this.tegn();
      };
      this.vedKurv = (e) => {
        const k = e.detail && e.detail.cart;
        if (!k || !Number.isFinite(k.total_price)) return;
        this.kurvGen = (this.kurvGen || 0) + 1; // en lesing av /cart.js som var underveis, gjelder ikke lenger (codex 23b runde 2, 2)
        this.kurvSum = k.total_price;
        this.tegn();
      };
      document.addEventListener('on:variant:change', this.vedVariant);
      document.addEventListener('on:cart:add', this.vedKurv);
      document.addEventListener('on:line-item:change', this.vedKurv);

      this.tegn();
      this.hentFraPris();
      if (this.t.pn) this.slaOpp(this.t.pn, false);
      if (this.venter) this.sendTilKunde(this.venter);
      else if (this.innlogget) this.hentKunde();
    }

    /** Postnummeret ved sidelasting (regelen ved innlogging, codex 19d). Gjest: nettleserens. Innlogget med et postnummer som venter på
     *  Lager fra dette besøket (skrevet som gjest, eller av denne kunden før lagringen ble bekreftet): det vinner og sendes. Ellers kundens
     *  postnummer fra metafeltet (GET kunde retter det etterpå), og uten det nettleserens. Et merke fra en annen kunde i samme fane forkastes. */
    startPostnummer(cfg) {
      this.venter = null;
      const lagret = lesLagret();
      if (!this.innlogget) return lagret;
      const m = lesMerke();
      if (m && (m.k === null || m.k === this.kunde)) {
        this.venter = m.pn;
        if (m.k === null) settMerke(m.pn, this.kunde); // merket følger kunden nå, så et kontobytte i fanen ikke tar det med
        lagrePostnummer(m.pn);
        return m.pn;
      }
      if (m) fjernMerke(null);
      return gyldigPn(cfg.kunde_postnummer) || lagret;
    }

    /** GET kunde: Lagers postnummer på kunden er fasit for innlogget kunde uten et postnummer som venter. Nettleserens erstattes av det.
     *  Har kunden valgt et postnummer mens lesingen gikk (gen), eller venter et på Lager, står det. Feil eller 401: det som vises, står. */
    async hentKunde() {
      if (!this.innlogget || this.henter) return;
      const t = this.t;
      const gen = t.gen;
      this.henter = true;
      let r;
      try {
        r = await proxyKall(`${this.proxy}/kunde`);
      } finally {
        this.henter = false;
      }
      if (this.t !== t || gen !== t.gen || t.utkast != null || this.venter || lesMerke()) return;
      const d = r.data;
      if (!r.ok || !d || d.innlogget !== true) return;
      const pn = gyldigPn(d.postnummer);
      if (!pn) return;
      // Nettleserens postnummer erstattes, men et nytt opprettes ikke uten at kunden har skrevet i denne nettleseren (ekomloven § 3-15 b,
      // codex 19d-2, 5). Kurven leser kundens metafelt i Liquid, så den trenger det ikke.
      const lagret = lesLagret();
      if (lagret && lagret !== pn) lagrePostnummer(pn);
      if (pn === t.pn) return;
      if (document.activeElement && document.activeElement.matches('[data-lp-postnummer]')) return; // aldri mens kunden skriver
      t.visning = 'med';
      this.velgPostnummer(pn, false);
    }

    /** Lagrer postnummeret på kunden i Lager. Én forespørsel om gangen, og den siste kunden valgte sendes til slutt. Merket fjernes når
     *  Lager har bekreftet (satt, uendret, eller 400: Lager kjenner ikke postnummeret, ingen grunn til å prøve igjen). 401, 429, 5xx og
     *  nettfeil: merket står, og neste side sender på nytt. */
    sendTilKunde(pn) {
      this.onsket = pn;
      if (this.sender) return;
      this.sender = (async () => {
        try {
          while (this.onsket) {
            const p = this.onsket;
            this.onsket = null;
            const r = await proxyKall(`${this.proxy}/postnummer`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ hendelse_id: uuid(), postnummer: p })
            });
            const bekreftet = r.ok && r.data && (r.data.status === 'satt' || r.data.status === 'uendret') && r.data.postnummer === p;
            if (bekreftet || r.status === 400 || r.status === 403) {
              fjernMerke(p);
              if (this.venter === p) this.venter = null;
            }
          }
        } finally {
          this.sender = null;
        }
      })();
    }

    /** Kunden har skrevet eller valgt lokasjon, og Lager har bekreftet postnummeret: i nettleseren, merket for innloggingen, og på kunden. */
    huskValg(pn) {
      lagrePostnummer(pn);
      settMerke(pn, this.innlogget ? this.kunde : null);
      if (this.innlogget) {
        this.venter = pn;
        this.sendTilKunde(pn);
      }
    }

    /** Blokken fjernes (tegnet på nytt): et åpent panel lukkes først (scroll-låsen og fokusfellen slippes), alle lyttere fjernes, og
     *  tidtakere og svar som er underveis gjelder ikke lenger (codex 19d-1, 6). */
    disconnectedCallback() {
      clearTimeout(this.vent);
      clearTimeout(this.midnattTimer);
      this.midnattTimer = null;
      this.t.sokId++;
      this.t.fraId++;
      this.t.lokNr++;
      window.removeEventListener('storage', this.vedLagring);
      window.removeEventListener('pageshow', this.vedTilbake);
      document.removeEventListener('on:variant:change', this.vedVariant);
      document.removeEventListener('on:cart:add', this.vedKurv);
      document.removeEventListener('on:line-item:change', this.vedKurv);
      this.removeEventListener('keydown', this.vedTast);
      for (const rot of [this, this.panel]) {
        rot.removeEventListener('input', this.vedSkriving);
        rot.removeEventListener('click', this.vedKlikk);
      }
      this.panel.removeEventListener('on:levering-produkt:before-close', this.vedForLukking);
      if (this.panel.hasAttribute('open')) this.panel.close();
      if (this.panel.parentNode === document.body) this.panel.remove();
      this.startet = false;
    }

    lagerUrl() {
      const u = String(this.dataset.lager || '').trim().replace(/\/+$/, '');
      return /^https:\/\/[a-z0-9.-]+(:\d+)?$/i.test(u) ? u : null;
    }

    /** «Fra»-prisen (laveste sonepris) fra et svar. Et svar fra et eldre snapshot enn det sist sette forkastes (som i handlekurvboksen). */
    taFraPris(s) {
      if (!s || !('fra_pris_ore' in s)) return;
      const v = typeof s.versjon === 'number' ? s.versjon : null;
      if (v != null && this.t.fraVersjon != null && v < this.t.fraVersjon) return;
      if (v != null) this.t.fraVersjon = v;
      this.t.fraPris = Number.isFinite(s.fra_pris_ore) && s.fra_pris_ore >= 0 ? s.fra_pris_ore : null;
    }

    /** Fra-prisen uten postnummer. Egen forespørsels-ID og samme frist som oppslaget: et eldre svar eller en hengende forespørsel kan aldri
     *  gjeninnføre en pris, og feiler den, vises meldingen om at levering ikke kan sjekkes (codex 19d-1, 3 og 4). Prisen fra denne
     *  sidevisningen står mens en ny henting pågår, høyst fristen. */
    async hentFraPris() {
      const t = this.t;
      const id = ++t.fraId;
      let tid = null;
      let svar = null;
      try {
        const url = this.lagerUrl();
        if (!url) throw new Error('levering: adressen til Lager mangler');
        const ctrl = new AbortController();
        tid = setTimeout(() => ctrl.abort(), FRIST_MS);
        const r = await fetch(`${url}/api/levering/oppslag`, { signal: ctrl.signal, headers: { Accept: 'application/json' } });
        if (!r.ok) throw new Error(`levering: fra-prisen svarte ${r.status}`);
        svar = await r.json();
      } catch (e) {
        console.log(e); // eslint-disable-line
        svar = null;
      } finally {
        clearTimeout(tid);
      }
      if (this.t !== t || id !== t.fraId) return;
      if (svar) {
        t.fraFeil = false;
        this.taFraPris(svar);
      } else {
        t.fraFeil = true;
        t.fraPris = null;
      }
      this.tegn();
    }

    /** Slår opp postnummeret. lagre: kunden skrev det eller valgte lokasjon, så det lagres i nettleseren når Lager kjenner det. */
    async slaOpp(pn, lagre) {
      const t = this.t;
      const id = ++t.sokId;
      // Ny visning av samme postnummer (panelet åpnet, tilbake-knappen): svaret fra denne sidevisningen står til det nye er her, så
      // knappen som åpnet panelet ikke tegnes på nytt. Feiler oppslaget, erstattes det av meldingen (aldri en pris uten svar).
      const rolig = !lagre && !!t.svar && t.svar.postnummer === pn && t.status !== 'soker';
      if (!rolig) {
        t.status = 'soker';
        t.svar = null;
        this.tegn();
      }
      let status;
      let svar = null;
      let tid = null;
      try {
        const url = this.lagerUrl();
        if (!url) throw new Error('levering: adressen til Lager mangler');
        const ctrl = new AbortController();
        tid = setTimeout(() => ctrl.abort(), FRIST_MS); // fristen gjelder til innholdet er lest
        const r = await fetch(`${url}/api/levering/oppslag?postnummer=${encodeURIComponent(pn)}`, { signal: ctrl.signal, headers: { Accept: 'application/json' } });
        if (r.status === 400) status = 'ukjent';
        else {
          if (!r.ok) throw new Error(`levering: oppslaget svarte ${r.status}`);
          svar = await r.json();
          if (!gyldigSvar(svar)) throw new Error('levering: ukjent svar fra oppslaget');
          if (!svar.sone) status = svar.versjon != null && !svar.sted ? 'ukjent' : 'ingen_sone';
          else if (svar.sone.datovalg === false) status = 'neste';
          else status = svar.dager.some((d) => d.ledig) ? 'klar' : 'ingen_dager';
        }
      } catch (e) {
        console.log(e); // eslint-disable-line
        status = 'utilgjengelig';
        svar = null;
      } finally {
        clearTimeout(tid);
      }
      // Et nyere oppslag, et annet postnummer eller en ny tilkobling gjelder: ingenting endres, heller ikke fra-prisen (codex 19d-1 runde 2, 1)
      if (this.t !== t || id !== t.sokId || pn !== t.pn) return;
      if (svar && svar.postnummer !== pn) return;
      if (svar) this.taFraPris(svar);
      t.status = status;
      t.svar = svar;
      // Kunden valgte postnummeret (lagre her eller tidligere, før en ny åpning slo opp på nytt): lagres når Lager bekrefter det (runde 2, 5)
      if ((lagre || t.lagreFor === pn) && KJENT.includes(status)) {
        this.huskValg(pn);
        t.lagreFor = null;
      }
      if (MED.includes(status)) t.visning = 'med';
      else if (status === 'ukjent') t.visning = 'uten';
      // Feltet i boksen forsvinner når sammendraget kommer: fokus flyttes til postnummeret i sammendraget, ikke til ingenting
      const aktiv = document.activeElement;
      const fraBoks = !!(aktiv && aktiv.closest && aktiv.closest('levering-produkt [data-lp-uten]') && this.contains(aktiv));
      this.tegn();
      if (fraBoks && t.visning === 'med') {
        const k = this.querySelector('[data-lp-med] [data-lp-apne="felt"]');
        if (k) k.focus();
      }
    }

    skrev(felt) {
      const t = this.t;
      const pn = felt.value.replace(/\D/g, '').slice(0, 4);
      if (felt.value !== pn) felt.value = pn;
      clearTimeout(this.vent);
      t.lok = null;
      t.lokNr++; // et posisjonssvar etter at kunden har skrevet selv, forkastes
      t.gen++; // kunden skriver: et svar fra GET kunde som er underveis, gjelder ikke (codex 19d-2, 4)
      if (pn.length < 4) {
        t.utkast = pn;
        t.sokId++; // et oppslag som er underveis, teller ikke
        this.tegn();
        return;
      }
      t.utkast = null;
      if (pn === t.pn && t.status !== 'soker' && t.status !== 'tom') {
        this.tegn();
        return;
      }
      this.velgPostnummer(pn, true, VENT_MS);
    }

    /** Ett sted der postnummeret som gjelder byttes (kunden skriver, lokasjon, annen fane, utkast forkastet): ventende tidtaker, oppslag og
     *  posisjonssøk fra før gjelder ikke lenger (codex 19d-1, 1). vent: millisekunder før oppslaget (kunden skriver). */
    velgPostnummer(pn, lagre, vent = 0) {
      const t = this.t;
      clearTimeout(this.vent);
      t.lokNr++;
      t.lok = null;
      t.utkast = null;
      if (pn !== t.pn) t.gen++;
      t.pn = pn;
      if (lagre) t.lagreFor = pn;
      else if (t.lagreFor !== pn) t.lagreFor = null;
      if (!vent) {
        this.tegn(); // feltene viser postnummeret med en gang, også når oppslaget er rolig (runde 2, 4)
        this.slaOpp(pn, lagre);
        return;
      }
      t.status = 'soker';
      t.svar = null;
      t.sokId++;
      this.tegn();
      this.vent = setTimeout(() => this.slaOpp(pn, lagre), vent);
    }

    apne(opener, tilFeltet) {
      this.t.utkast = null;
      this.tegn();
      const felt = this.panel.querySelector('[data-lp-postnummer]');
      this.apnetMed = tilFeltet ? '[data-lp-med] [data-lp-apne="felt"]' : '[data-lp-apne]:not([data-lp-apne="felt"])';
      this.panel.open(opener, tilFeltet || !this.t.pn ? felt : this.panel);
      // Hver åpning slår opp på nytt, så panelet aldri viser alternativer fra en tidligere visning
      this.hentFraPris();
      if (this.t.pn) this.slaOpp(this.t.pn, false);
    }

    velgFane(navn, fokus) {
      this.fane = navn;
      this.querySelectorAll('[data-lp-fane]').forEach((b) => {
        const valgt = b.dataset.lpFane === navn;
        b.setAttribute('aria-selected', valgt ? 'true' : 'false');
        b.tabIndex = valgt ? 0 : -1;
        if (valgt && fokus) b.focus();
      });
      this.querySelectorAll('[data-lp-fanepanel]').forEach((p) => { p.hidden = p.dataset.lpFanepanel !== navn; });
    }

    /** «Velg min lokasjon»: nettleserens posisjon og Kartverkets punktsøk (samme som finnLokasjon i assets/levering-valg.js). */
    finnLokasjon() {
      const t = this.t;
      const nr = ++t.lokNr;
      const feil = () => {
        if (nr !== t.lokNr) return;
        t.lok = { status: 'feil' };
        this.tegn();
      };
      if (!navigator.geolocation) return feil();
      t.lok = { status: 'soker' };
      this.tegn();
      navigator.geolocation.getCurrentPosition(async (pos) => {
        try {
          if (nr !== t.lokNr) return;
          const { latitude: lat, longitude: lon } = pos.coords;
          for (const radius of [250, 2000]) {
            const r = await fetch(`https://ws.geonorge.no/adresser/v1/punktsok?lat=${lat}&lon=${lon}&radius=${radius}&koordsys=4258&treffPerSide=10`);
            if (!r.ok) throw new Error(`punktsok ${r.status}`);
            const d = await r.json();
            const treff = (d.adresser || []).filter((x) => /^\d{4}$/.test(x.postnummer || ''))
              .sort((x, y) => (x.meterDistanseTilPunkt ?? 0) - (y.meterDistanseTilPunkt ?? 0))[0];
            if (treff) {
              if (nr !== t.lokNr) return;
              this.velgPostnummer(treff.postnummer, true);
              return;
            }
          }
          feil();
        } catch (e) {
          console.log(e); // eslint-disable-line
          feil();
        }
      }, feil, { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 });
    }

    /** Kampanjen: kurvsummen leses på nytt fra /cart.js (tilbake fra sidebufferen). Feiler lesingen, står den gamle. */
    async hentKurvSum() {
      const t = this.t;
      const gen = this.kurvGen || 0;
      try {
        const rot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        const r = await fetch(`${rot}cart.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' });
        if (!r.ok) return;
        const k = await r.json();
        if (this.t !== t || gen !== (this.kurvGen || 0) || !Number.isFinite(k.total_price)) return; // en kurvhendelse kom imens: den er ferskere
        this.kurvSum = k.total_price;
        this.tegn();
      } catch (e) {
        console.log(e); // eslint-disable-line
      }
    }

    /** Kampanjen (serie 23): summen boksen regner med, og prisen som tekst (prisordet når den er gratis, ellers kroner). */
    kampSum() {
      return this.kurvSum + this.varePris;
    }
    gratis(ore) {
      return kampanjeGratis(this.kampanje, this.kampSum(), ore);
    }
    prisOrd(ore) {
      return this.gratis(ore) ? this.kampanje.prisord : kroner(ore);
    }

    /** Alle prisene i sonen (dager og tidsrom). Mer enn én gir «fra» foran den laveste; er den laveste gratis i kampanjen, står prisordet. */
    prisTekst(s) {
      const p = new Set([s.sone.pris_ore]);
      for (const d of s.dager) for (const x of d.tidsrom) if (Number.isFinite(x.pris_ore)) p.add(x.pris_ore);
      const min = Math.min(...p);
      if (this.gratis(min)) return this.kampanje.prisord;
      return p.size > 1 ? fyll(this.tk.fra, { pris: kroner(min) }) : kroner(min);
    }

    /** Prismatrisen fra Lager som rader i panelet: dag, kveld, lørdag og innbæring (som tillegg når det er likt overalt). Kampanjen: radene
     *  dag, kveld og lørdag viser prisordet når de er gratis; innbæring er aldri gratis, og er gateplan gratis, står innbæringens hele pris
     *  («fra 1 290 kr»), ikke tillegget, som ellers ville lest som prisen. */
    priser(s) {
      const tk = this.tk;
      const m = s.sone.priser;
      const g = m && m.gateplan;
      if (!g) return '';
      const rader = [];
      const tall = (v) => Number.isFinite(v);
      if (tall(g.kveld) || tall(g.lordag)) rader.push([tk.dag, this.prisOrd(g.dag)]);
      if (tall(g.kveld)) rader.push([tk.kveld, this.prisOrd(g.kveld)]);
      if (tall(g.lordag)) rader.push([tk.lordag, this.prisOrd(g.lordag)]);
      const i = m.innbaering;
      if (i) {
        const nokler = ['dag', 'kveld', 'lordag'].filter((k) => tall(g[k]) && tall(i[k]));
        const tillegg = new Set(nokler.map((k) => i[k] - g[k]));
        const gateplanGratis = nokler.some((k) => this.gratis(g[k]));
        const innPriser = new Set(nokler.map((k) => i[k]));
        if (nokler.length && gateplanGratis) rader.push([tk.innbaering, innPriser.size > 1 ? fyll(tk.fra, { pris: kroner(Math.min(...innPriser)) }) : kroner([...innPriser][0])]);
        else if (nokler.length && tillegg.size === 1 && [...tillegg][0] > 0) rader.push([tk.innbaering, fyll(tk.tillegg, { pris: kroner([...tillegg][0]) })]);
        else if (nokler.length) rader.push([tk.innbaering, fyll(tk.fra, { pris: kroner(Math.min(...innPriser)) })]);
      }
      if (!rader.length) return '';
      return `<dl class="lp__priser">${rader.map(([n, v]) => `<div><dt>${esc(n)}</dt><dd>${esc(v)}</dd></div>`).join('')}</dl>`;
    }

    /** Sammendraget i boksen når postnummeret er kjent. */
    sammendrag() {
      const { t, tk } = this;
      const s = t.svar;
      const sted = `${t.pn}${s && s.sted ? ` ${stedsnavn(s.sted)}` : ''}`;
      const knapp = `<button type="button" class="lp__pn" data-lp-apne="felt" aria-haspopup="dialog" aria-label="${esc(fyll(tk.endre, { postnummer: sted }))}">${esc(sted)}</button>`;
      const linje = (forst, resten) => `<p>${esc(forst)} ${knapp}</p>${resten}`;
      if (t.status === 'klar') {
        const forste = s.dager.find((d) => d.ledig);
        return linje(tk.raskeste, `<p class="lp__sterk">${esc(fyll(tk.tidligst, { dag: dagTekst(forste.dato) }))}, ${esc(this.prisTekst(s))}</p>`);
      }
      if (t.status === 'neste') return linje(tk.levering_til, `<p class="lp__sterk">${esc(tk.neste)}, ${esc(this.prisOrd(s.sone.pris_ore))}</p>`);
      if (t.status === 'ingen_dager') return linje(tk.levering_til, `<p class="lp__sterk">${esc(tk.hjemlevering)}, ${esc(this.prisOrd(s.sone.pris_ore))}</p><p>${esc(tk.ingen_dager)}</p>`);
      if (t.status === 'ingen_sone') return linje(tk.levering_til, `<p class="lp__tekst">${esc(s.ikke_tekst || tk.ikke_levering)}</p>`);
      if (t.status === 'utilgjengelig') return linje(tk.levering_til, `<p>${esc(tk.utilgjengelig)}</p>`);
      return linje(tk.levering_til, `<p class="lp__dempet" aria-busy="true">${esc(tk.soker)}</p>`);
    }

    /** Hjemlevering i panelet, for postnummeret i feltet. */
    leverAlt() {
      const { t, tk } = this;
      const s = t.svar;
      const fra = this.fraTekst();
      const hode = (navn, pris) => `<div class="lp__alt-hode"><p class="lp__alt-navn">${esc(navn)}</p>${pris ? `<p class="lp__alt-pris">${esc(pris)}</p>` : ''}</div>`;
      if (!t.pn && t.utkast == null && t.fraFeil) return hode(tk.hjemlevering, '') + `<p>${esc(tk.utilgjengelig)}</p>`;
      if (t.utkast != null || !t.pn) return hode(tk.hjemlevering, fra) + `<p class="lp__dempet">${esc(tk.skriv)}</p>`;
      if (t.status === 'klar') {
        const forste = s.dager.find((d) => d.ledig);
        return hode(s.sone.navn, this.prisTekst(s)) + `<p class="lp__dempet">${esc(fyll(tk.tidligst, { dag: dagTekst(forste.dato) }))}</p>` + this.priser(s);
      }
      if (t.status === 'neste') return hode(s.sone.navn, this.prisOrd(s.sone.pris_ore)) + `<p class="lp__sterk">${esc(tk.neste)}</p><p class="lp__dempet lp__tekst">${esc(s.neste_tekst || tk.neste_reserve)}</p>`;
      if (t.status === 'ingen_dager') return hode(s.sone.navn, this.prisOrd(s.sone.pris_ore)) + `<p>${esc(tk.ingen_dager)}</p>` + this.priser(s);
      if (t.status === 'ingen_sone') return hode(tk.hjemlevering, '') + `<p class="lp__tekst">${esc(s.ikke_tekst || tk.ikke_levering)}</p>`;
      if (t.status === 'ukjent') return hode(tk.hjemlevering, '') + `<p>${esc(tk.ukjent)}</p>`;
      if (t.status === 'utilgjengelig') return hode(tk.hjemlevering, '') + `<p>${esc(tk.utilgjengelig)}</p>`;
      return hode(tk.hjemlevering, '') + `<p class="visually-hidden">${esc(tk.soker)}</p>`;
    }

    /** «Fra»-prisen uten postnummer: prisordet når den laveste prisen er gratis i kampanjen (planen 23b), ellers «fra 590 kr». */
    fraTekst() {
      const { t, tk } = this;
      if (t.fraPris == null) return '';
      return this.gratis(t.fraPris) ? this.kampanje.prisord : fyll(tk.fra, { pris: kroner(t.fraPris) });
    }

    /** Tegner boksen og panelet fra tilstanden. Idempotent, og innhold byttes bare når det er endret (så skjermlesere ikke leser opp alt på nytt). */
    tegn() {
      const { t, tk } = this;
      const s = t.svar;
      // Kampanjelinja under boksen og øverst i panelet mens kampanjen er aktiv (serie 23); en åpen side tegnes på nytt etter midnatt
      const kampTekst = kampanjeAktiv(this.kampanje) && this.kampanje.linje ? this.kampanje.linje : '';
      if (kampanjeAktiv(this.kampanje) && !this.midnattTimer) { // etter aktiv kampanje, ikke etter linjeteksten (codex 23b runde 2, 4)
        this.midnattTimer = setTimeout(() => { this.midnattTimer = null; if (this.isConnected) this.tegn(); }, Math.min(msTilMidnatt(new Date()), 2147483647));
      }
      for (const el of [...this.querySelectorAll('[data-lp-kampanje]'), ...this.panel.querySelectorAll('[data-lp-kampanje]')]) {
        if (el.textContent !== kampTekst) el.textContent = kampTekst;
        el.hidden = !kampTekst;
      }
      const sett = (el, html) => {
        if (!el || el.lpHtml === html) return;
        el.lpHtml = html;
        el.innerHTML = html;
      };
      const alle = (sel) => [...this.querySelectorAll(sel), ...this.panel.querySelectorAll(sel)];
      for (const f of alle('[data-lp-postnummer]')) if (document.activeElement !== f) f.value = t.utkast ?? t.pn;
      const sted = s && s.sted && s.postnummer === t.pn && t.utkast == null ? stedsnavn(s.sted) : '';
      for (const el of alle('[data-lp-sted]')) el.textContent = sted;
      for (const b of alle('[data-lp-lokasjon]')) b.setAttribute('aria-busy', t.lok && t.lok.status === 'soker' ? 'true' : 'false');
      let melding = '';
      if (t.lok && t.lok.status === 'feil') melding = `<p>${esc(tk.lokasjon_feil)}</p>`;
      else if (t.lok) melding = `<p class="visually-hidden">${esc(tk.lokasjon_soker)}</p>`;
      else if (t.utkast == null && t.status === 'ukjent') melding = `<p>${esc(tk.ukjent)}</p>`;
      else if (t.utkast == null && t.status === 'soker') melding = `<p class="visually-hidden">${esc(tk.soker)}</p>`;
      else if (!t.pn && t.utkast == null && t.fraFeil) melding = `<p>${esc(tk.utilgjengelig)}</p>`;
      for (const el of alle('[data-lp-melding]')) sett(el, melding);

      const fra = this.fraTekst();
      const fraLinje = this.querySelector('[data-lp-fra-linje]');
      if (fraLinje) {
        fraLinje.textContent = fra ? `${tk.hjemlevering} ${fra}` : '';
        fraLinje.hidden = !fra;
      }
      const med = t.visning === 'med' && !!t.pn;
      const uten = this.querySelector('[data-lp-uten]');
      if (uten) uten.hidden = med;
      const medBoks = this.querySelector('[data-lp-med-boks]') || this.querySelector('[data-lp-med]');
      if (medBoks) medBoks.hidden = !med;
      // Har postnummerknappen i sammendraget fokus når sammendraget tegnes på nytt (et sent svar), får den nye knappen fokus, eller
      // feltet i boksen når sammendraget er skjult (codex 19d-1 runde 2, 2)
      const aktiv = document.activeElement;
      const fokusPn = !!(aktiv && this.contains(aktiv) && aktiv.matches('[data-lp-med] [data-lp-apne="felt"]'));
      if (med) sett(this.querySelector('[data-lp-med]'), this.sammendrag());
      if (fokusPn && !aktiv.isConnected) {
        const ny = med ? this.querySelector('[data-lp-med] [data-lp-apne="felt"]') : this.querySelector('[data-lp-uten] [data-lp-postnummer]');
        if (ny) ny.focus();
      } else if (fokusPn && !med) {
        const felt = this.querySelector('[data-lp-uten] [data-lp-postnummer]');
        if (felt) felt.focus();
      }
      sett(this.panel.querySelector('[data-lp-lever-alt]'), this.leverAlt());
      this.dataset.status = t.status; // for testene
    }
  }
  customElements.define('levering-produkt', LeveringProdukt);
}
